import React, { useState, useEffect, useRef } from "react";
import "./HREvents.css";
import { useUserTokenSelector } from "store/reducers";
import Loading from "../../components/Utilities/Loading/Loading";
import { GrDownload } from "react-icons/gr";
import { CiCircleInfo } from "react-icons/ci";
import { FaChevronRight, FaChevronLeft } from "react-icons/fa";
import {LineChart,Line,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,} from "recharts";
import { Progress } from "antd";
import { useInView } from "react-intersection-observer";
import { getEventDetails } from "actions/assessment";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "store/actions";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Template } from "Containers/Overview/template";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">     
        <p className="label">{`${label}`}</p>
        <p className="value">{`Value: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

export const HREvents = ({ adminCorporateId }) => {
  const [allWorkshopsData, setAllWorkshopsData] = useState([]);
  const [filteredWorkshopsData, setFilteredWorkshopsData] = useState([]);
  const [selectedWorkshopData, setSelectedWorkshopData] = useState(null); // Holds the data for the currently displayed workshop
  const [selectedYear, setSelectedYear] = useState("All Time");
  const [isWorkshopActive, setIsWorkshopActive] = useState(false); // For intersection observer, to trigger animations/effects
  const [isSplitActive, setIsSplitActive] = useState(false); // For intersection observer, for the monthly split chart
  const [currentWorkshopIndex, setCurrentWorkshopIndex] = useState(0); // Index for workshop carousel
  const [monthlyEngagementData, setMonthlyEngagementData] = useState([]); // Data for the monthly engagement chart
  const [selectTimeRange, setSelectTimeRange] = useState("all"); // Default to 'all' for time range filter
  const token = useUserTokenSelector();
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  

  const [userInfo, setUserInfo] = useState({
    signups: {
      total: 0,
      previous: 0,
    },
    active: {
      total: 0,
      previous: 0,
    },
    logins: {
      total: 0,
      previous: 0,
    },
  });

  // Month names for chart labels and potential future filtering
  const monthNames = [
    "All",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dispatch = useDispatch();
  // Fallback data for the monthly engagement chart if API data is unavailable or empty
  const engagementMonthlyFallback = Array(12)
    .fill(null)
    .map((_, index) => ({
      name: monthNames[index + 1],
      value: 0, // Default to 0 if no data
    }));

  // State for dropdown filters
  const [availableYears, setAvailableYears] = useState(["All Time"]);

  // Intersection observer for workshop participation progress circle
  const [workshopRef, workshopInView] = useInView({ threshold: 0.7 });
  useEffect(() => {
    if (workshopInView) setIsWorkshopActive(true);
  }, [workshopInView]);

  // Intersection observer for monthly split chart
  const [splitRef, splitInView] = useInView({ threshold: 0.7 });
  useEffect(() => {
    if (splitInView) setIsSplitActive(true);
  }, [splitInView]);

  // Time range filter setup
  const timeRangeMap = new Map([
    ["Today", "today"],
    ["This Week", "7d"],
    ["This Month", "30d"],
    ["Last 3 months", "3m"],
    ["Last 6 months", "6m"],
    ["Last Year", "lastYear"],
    ["This Year", "1y"],
    ["All Time", "all"],
    ["Custom", "custom"],
  ]);
  const timeRangeArray = [
    "All Time",
    "Today",
    "This Week",
    "This Month",
    "Last 3 months",
    "Last 6 months",
    "Last Year",
    "This Year",
    "Custom",
  ];

  const handleTimeRangeChange = (e) => {
    const value = e.target.value;
    setSelectTimeRange(value);
    if (value === "custom") {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  const handleCustomDateChange = (date) => {
    if (Array.isArray(date)) {
      const [startDate, endDate] = date;
      setCustomDateRange([startDate, endDate]);
      if (startDate && endDate) {
        setShowCustomCalendar(false);
      }
      return;
    }
    setCustomDateRange(date);
  };

  const getCustomDates = () => {
    if (Array.isArray(customDateRange)) {
      const [startDate, endDate] = customDateRange;
      return { startDate, endDate, singleDate: startDate };
    }
    return { startDate: null, endDate: null, singleDate: customDateRange };
  };

  const mapTimeRangeToApiValue = (timeRange) =>
    timeRangeMap.get(timeRange) || "all";

  // Process workshop data for monthly engagement chart
  useEffect(() => {
    dispatch(startLoading());
    const processWorkshopData = (data) => {
      if (allWorkshopsData && allWorkshopsData.length > 0) {
        const monthlyData = Array(12)
          .fill(null)
          .map((_, index) => ({
            name: monthNames[index + 1],
            value: 0,
            attendees: new Set(),
          }));

        allWorkshopsData.forEach((workshop) => {
          const eventDate = new Date(workshop.eventDate);
          const monthIndex = eventDate.getMonth();

          if (
            workshop.workshopAttendanceList &&
            workshop.workshopAttendanceList.length > 0
          ) {
            workshop.workshopAttendanceList.forEach((attendee) => {
              monthlyData[monthIndex].attendees.add(attendee.userName);
            });
          }
        });

        const processedData = monthlyData.map((monthEntry) => ({
          name: monthEntry.name,
          value: monthEntry.attendees.size,
        }));

        setMonthlyEngagementData(processedData);
      } else {
        // Use fallback if API data is empty or not yet loaded
        setMonthlyEngagementData(engagementMonthlyFallback);
      }
    };
    processWorkshopData();
    dispatch(stopLoading());
  }, [allWorkshopsData, monthNames, engagementMonthlyFallback]);

  const fetchWorkshopDetails = async () => {
    if (!adminCorporateId || !token) return;

    try {
      let apiTimeRange = mapTimeRangeToApiValue(selectTimeRange);
      let startDate = null;
      let endDate = null;

      const res = await getEventDetails(
        token,
        adminCorporateId,
        selectTimeRange,
        customDateRange
      );
      if (res.data.status === "success") {
        let workshops = res.data.data;
        workshops.sort(
          (a, b) => new Date(b.eventDate) - new Date(a.eventDate)
        ); // Newest first

        setAllWorkshopsData(workshops);

        const yearsSet = new Set();
        workshops.forEach((w) => {
          const eventDateObj = new Date(w.eventDate);
          if (!isNaN(eventDateObj)) {
            yearsSet.add(eventDateObj.getFullYear().toString());
          }
        });
        setAvailableYears([
          "All Time",
          ...Array.from(yearsSet).sort((a, b) => b - a),
        ]); // Sort years descending
      } else {
        console.error("API returned an error:", response.data.error);
        setAllWorkshopsData([]);
        setAvailableYears(["All Time"]);
        setCurrentWorkshopIndex(0);
      }
    } catch (error) {
      console.error("Error fetching workshop details:", error);
      setAllWorkshopsData([]);
      setAvailableYears(["All Time"]);
      setCurrentWorkshopIndex(0);
    } finally {
      dispatch(stopLoading())
    }
  };

  // Effect to fetch data from the API
  useEffect(() => {
    const { startDate, endDate } = getCustomDates();
    const isCustomDateValid =
      selectTimeRange !== "custom" || (startDate && endDate);

    if (!isCustomDateValid) return;
    dispatch(startLoading());
    fetchWorkshopDetails();
    dispatch(stopLoading());
  }, [adminCorporateId, token, selectTimeRange, customDateRange]);

  // Effect to filter workshops based on selected year
  useEffect(() => {
    dispatch(startLoading());
    let workshopsToFilter = [...allWorkshopsData];

    if (selectedYear !== "All Time") {
      workshopsToFilter = workshopsToFilter.filter(
        (w) => new Date(w.eventDate).getFullYear().toString() === selectedYear
      );
    }

    setFilteredWorkshopsData(workshopsToFilter);

    if (
      currentWorkshopIndex >= workshopsToFilter.length &&
      workshopsToFilter.length > 0
    ) {
      setCurrentWorkshopIndex(0);
    } else if (workshopsToFilter.length === 0) {
      setCurrentWorkshopIndex(0);
    }
    dispatch(stopLoading());
  }, [allWorkshopsData, selectedYear, currentWorkshopIndex]);

  // Effect to select the specific workshop data to display in the carousel
  useEffect(() => {
    dispatch(startLoading());
    let workshopToDisplay = null;
    if (
      filteredWorkshopsData.length > 0 &&
      currentWorkshopIndex < filteredWorkshopsData.length
    ) {
      workshopToDisplay = filteredWorkshopsData[currentWorkshopIndex];
    }
    setSelectedWorkshopData(workshopToDisplay);
    dispatch(stopLoading());
  }, [filteredWorkshopsData, currentWorkshopIndex]);

  // Handlers for workshop carousel navigation
  const handleNextWorkshop = () => {
    if (filteredWorkshopsData.length === 0) return;
    setCurrentWorkshopIndex((prevIndex) =>
      prevIndex + 1 >= filteredWorkshopsData.length ? 0 : prevIndex + 1
    );
  };

  const handlePreviousWorkshop = () => {
    if (filteredWorkshopsData.length === 0) return;
    setCurrentWorkshopIndex((prevIndex) =>
      prevIndex - 1 < 0 ? filteredWorkshopsData.length - 1 : prevIndex - 1
    );
  };

  const workshopDisplayName =
    selectedWorkshopData?.eventName ||
    (filteredWorkshopsData.length > 0
      ? filteredWorkshopsData[0]?.eventName
      : "No Workshop Data");

  const hrEventData = () => {
    const dataJson = {
      signups: userInfo.signups,
      active: userInfo.active,
      logins: userInfo.logins,
      overviewItems: [],
      contentTrends: [],
      engagement: [],
      distribution: [],
      engagedEmp: [],
      allWorkshopsData: allWorkshopsData,
      filteredWorkshopsData: filteredWorkshopsData,
      selectedWorkshopData: selectedWorkshopData,
      monthlyEngagementData: monthlyEngagementData,
    };
    return dataJson;
  };

  const handleShowReport = () => {
    setIsDownloading(true);
    setShowReport(true);
    dispatch(startLoading());
  };

  const handleReportClose = () => {
    setIsDownloading(false);
    setShowReport(false);
    dispatch(stopLoading());
  };

  return (
    <div className="events">
      {showCustomCalendar && (
        <div
          className="Events-overlay-background"
          onClick={() => setShowCustomCalendar(false)}
          aria-label="Close calendar"
        ></div>
      )}
        <>
          <div>
            <div className="events-top">
              <div className="overview-filter">
                <select
                  id="timeRangeFilter"
                  value={selectTimeRange}
                  onChange={handleTimeRangeChange}
                  // onChange={(e) => {
                  //   const newTimeRange = e.target.value;
                  //   setSelectTimeRange(newTimeRange);
                  //   // Adjust selectedYear based on newTimeRange
                  //   if (newTimeRange === "1y") {
                  //     setSelectedYear(new Date().getFullYear().toString());
                  //   } else if (newTimeRange !== "all") {
                  //     setSelectedYear("All Time"); // Reset year if a specific short-term range is chosen
                  //   }
                  // }}
                >
                  {timeRangeArray.map((time) => (
                    <option key={time} value={mapTimeRangeToApiValue(time)}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
              <div className={`download-div ${isDownloading ? "disabled" : ""}`} onClick={!isDownloading ? handleShowReport : null}>
                <GrDownload />
                <span>Download</span>
              </div>
            </div>
            <div id="events">
              {filteredWorkshopsData.length === 0 ? (
                <div className="no-data-message">
                  No workshop data available for the selected filters.
                </div>
              ) : (
                <>
                  <div className="event-divs">
                    {showCustomCalendar && (
                      <div className="custom-calendar-container">
                        <Calendar
                          onChange={handleCustomDateChange}
                          value={customDateRange}
                          selectRange
                        />
                      </div>
                    )}
                    <div className="event-div participation-stats">
                      <div className="event-top">
                        {filteredWorkshopsData.length > 1 &&
                          currentWorkshopIndex > 0 && (
                            <FaChevronLeft
                              className="event-arrowIcon event-arrowIcon-left"
                              onClick={handlePreviousWorkshop}
                            />
                          )}
                        <span>{workshopDisplayName} Participation</span>
                        <CiCircleInfo className="event-infoIcon" />
                        {filteredWorkshopsData.length > 1 &&
                          currentWorkshopIndex <
                            filteredWorkshopsData.length - 1 && (
                            <FaChevronRight
                              className="event-arrowIcon event-arrowIcon-right"
                              onClick={handleNextWorkshop}
                            />
                          )}
                      </div>
                      <div className="event-bottom" ref={workshopRef}>
                        <div className="progress-div participation-progress">
                          <Progress
                            type="circle"
                            percent={
                              isWorkshopActive
                                ? selectedWorkshopData?.participationRate || 0
                                : 0
                            }
                            strokeColor={"#49AF7C"}
                            strokeWidth={12}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="workshop-div">
                      <div className="workshop-top">
                        <span>{workshopDisplayName} Attendee’s List</span>
                        <CiCircleInfo className="event-infoIcon" />
                      </div>
                      <div className="workshop-table">
                        <div className="workshop-head">
                          <div className="wname">
                            <p>Name</p>
                          </div>
                          <div className="wdept">
                            <p>Department</p>
                          </div>
                          <div className="wlocation">
                            <p>Location</p>
                          </div>
                          <div className="wpart">
                            <p>Participation</p>
                          </div>
                        </div>
                        <div className="workshop-body">
                          {selectedWorkshopData?.workshopAttendanceList
                            ?.length > 0 ? (
                            selectedWorkshopData.workshopAttendanceList.map(
                              (attendee, index) => (
                                <div
                                  className="attendee-div"
                                  key={`${attendee.userName}-${index}`}
                                >
                                  <div className="wname">
                                    <p>
                                      <span className="mobile-head">
                                        Name:{" "}
                                      </span>
                                      {attendee.userName}
                                    </p>
                                  </div>
                                  <div className="wdept">
                                    <p>
                                      <span className="mobile-head">
                                        Department:{" "}
                                      </span>
                                      {attendee.department || "N/A"} Team
                                    </p>
                                  </div>
                                  <div className="wlocation">
                                    <p>
                                      <span className="mobile-head">
                                        Location:{" "}
                                      </span>
                                      {attendee.location || "N/A"}
                                    </p>
                                  </div>
                                  <div className="wpart">
                                    <p>
                                      <span className="mobile-head">
                                        Participation:{" "}
                                      </span>
                                      {attendee.participationInMins} mins
                                    </p>
                                  </div>
                                </div>
                              )
                            )
                          ) : (
                            <div className="no-attendees-message">
                              No attendees for this workshop.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="event-divs">
                    <div className="event-div graph">
                      <div className="event-top">
                        <span>Disengaged Employees</span>
                        <CiCircleInfo className="event-infoIcon" />
                      </div>
                      <div className="emp-divs">
                        {selectedWorkshopData?.leastEngagedEmployees?.length >
                        0 ? (
                          selectedWorkshopData.leastEngagedEmployees.map(
                            (emp, i) => (
                              <div
                                key={`${emp.userName}-least-${i}`}
                                className="emp-div"
                              >
                                <div className="emp-rec dis"></div>
                                <div className="emp-info">
                                  <p>{emp.userName}</p>
                                  <p>{emp.participationInMins} mins</p>
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className="no-data-small">No data</div>
                        )}
                      </div>
                    </div>
                    <div className="event-div graph">
                      <div className="event-top">
                        <span>Most Participated Employees</span>
                        <CiCircleInfo className="event-infoIcon" />
                      </div>
                      <div className="emp-divs">
                        {selectedWorkshopData?.mostEngagedEmployees?.length >
                        0 ? (
                          selectedWorkshopData.mostEngagedEmployees.map(
                            (emp, i) => (
                              <div
                                key={`${emp.userName}-most-${i}`}
                                className="emp-div"
                              >
                                <div className="emp-rec"></div>
                                <div className="emp-info">
                                  <p>{emp.userName}</p>
                                  <p>{emp.participationInMins} mins</p>
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className="no-data-small">No data</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="event-div cmp">
                      <div className="eng-top">
                        <span>Event Engagement Monthly Split</span>
                        <CiCircleInfo className="event-infoIcon" />
                      </div>
                      <div className="event-management-chat" ref={splitRef}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthlyEngagementData}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              horizontal={true}
                              vertical={false}
                            />
                            <XAxis dataKey="name" />
                            <YAxis
                              allowDecimals={false}
                              domain={[
                                "dataMin",
                                (dataMax) =>
                                  dataMax === 0 ? 10 : Math.ceil(dataMax * 1.1),
                              ]}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            {isSplitActive && (
                              <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#C4EDD9"
                                strokeWidth={3}
                                dot={(props) => (
                                  <circle
                                    cx={props.cx}
                                    cy={props.cy}
                                    r={6}
                                    fill={"#49AF7C"}
                                  />
                                )}
                              />
                            )}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                </>
              )}
            </div>
          </div>
        </>
      {showReport && <Template data={hrEventData()} close={handleReportClose} />}
    </div>
  );
};

export default HREvents;
